/**
 * pages/goalsShared.ts — the vocabulary and the derivations the three goals screens share.
 *
 * A non-component module beside the pages, in the shape `accessTypes.ts` already established. It exists
 * so that the one judgement this feature turns on — *may this key result be posted again?* — has ONE
 * definition rather than a copy on each screen that could answer it.
 *
 * Two later arrivals are here for the same reason rather than in the component that first needed them:
 *
 *   - **The confidence arithmetic.** The wire carries the score as a whole number 1–100 and every screen
 *     shows it as the decimal Atlassian puts in its status chip ("At risk 0.5"). The form multiplies on
 *     the way out and divides on the way in, and the review line divides again — three sites, one
 *     conversion, or they will one day disagree about what 0.55 means.
 *   - **How a metric figure is written down.** A key result's number is a percentage, a currency amount
 *     or a plain count, and the field, its hint and the review line must all say it the same way.
 *     ⚠️ The *mapping* from Atlassian's opaque `metric.type` to one of those three lives on the SERVER
 *     (`server/goals/metrics.ts`) and is carried to us as `kind`; nothing here re-derives it.
 *
 * `useAsync` is the fourth copy of the shape `Dashboard.tsx` documents, and is here rather than there
 * because a page reaching into another page for a hook is worse than fifteen shared lines.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError, type DraftInput, type GoalCheckinState, type ItemView } from "../lib/api";
import type { Tone } from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * Loading — the three failure shapes a screen must keep apart: denied, failed, and not here yet.
 * ------------------------------------------------------------------------------------------------- */

export interface Async<T> {
  readonly data: T | null;
  readonly error: ApiError | null;
  readonly loading: boolean;
}

export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key: string,
): Async<T> & { readonly reload: () => void; readonly attempt: number } {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);

  // Clearing belongs to a change of SUBJECT, not to a re-read — the reason `Dashboard.tsx` gives at
  // length. A retry leaves the rows on screen until the new ones land.
  useEffect(() => {
    setState({ data: null, error: null, loading: true });
  }, [key]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    load(controller.signal)
      .then((data) => {
        if (live) setState({ data, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        const error = cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        // A refusal takes the data away — the reader has just been told they may not see it. Anything
        // else keeps what is already on screen and says so above it.
        setState((previous) => ({
          data: error.isPermissionDenied ? null : previous.data,
          error,
          loading: false,
        }));
      });

    return () => {
      live = false;
      controller.abort();
    };
    // `load` is re-created every render; `key` is what says the request actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);
  return { ...state, reload, attempt };
}

/* ---------------------------------------------------------------------------------------------------
 * "We do not hold this" and "we could not read this" are different facts
 * ------------------------------------------------------------------------------------------------- */

/**
 * What a synchronised field says when the sync did not bring it back.
 *
 * `06-modules.md` §5.3 R2: a field that could not be read is rendered as this and **never as an em
 * dash**, because the dash is already how this application says "there is no value" — a job that has
 * never run, a check-in that has not ended. Merging the two tells a reader that Atlassian holds no owner
 * for an objective when the truth is that we did not manage to ask.
 */
export const NOT_READ = "Not read";

export function orNotRead(value: string | null | undefined): string {
  return value === null || value === undefined || value.trim() === "" ? NOT_READ : value;
}

/**
 * A number from the remote system, or `NOT_READ`. Zero is a reading; only absence is not.
 *
 * For a figure belonging to a key result's METRIC, reach for `formatMetricValue` instead: it is the
 * kind-aware member of this pair, and a metric written without its per-cent sign or its thousands is a
 * figure a person has to decode before they can approve it.
 */
export function numberOrNotRead(value: number | null | undefined): string {
  return value === null || value === undefined || Number.isNaN(value) ? NOT_READ : String(value);
}

/* ---------------------------------------------------------------------------------------------------
 * The traffic-light
 * ------------------------------------------------------------------------------------------------- */

const STATE_LABELS: Readonly<Record<string, string>> = {
  on_track: "On track",
  at_risk: "At risk",
  off_track: "Off track",
  // The wider vocabulary Atlassian REPORTS and this connector cannot set. Named rather than left to a
  // prettifier so that a key result sitting in one of them reads as a state rather than as a wire value.
  pending: "Not started",
  paused: "Paused",
  done: "Done",
  cancelled: "Cancelled",
};

export function goalStateLabel(state: string | null | undefined): string {
  if (state === null || state === undefined || state === "") return NOT_READ;
  const normalised = state.toLowerCase();
  return STATE_LABELS[normalised] ?? state;
}

export function goalStateTone(state: string | null | undefined): Tone {
  switch ((state ?? "").toLowerCase()) {
    case "on_track":
    case "done":
      return "success";
    case "at_risk":
      return "warning";
    case "off_track":
    case "cancelled":
      return "danger";
    default:
      return "neutral";
  }
}

/** The three a person may choose, with the sentence that distinguishes them. */
export const CHECKIN_STATE_OPTIONS: readonly {
  readonly value: GoalCheckinState;
  readonly label: string;
  readonly hint: string;
}[] = [
  { value: "on_track", label: "On track", hint: "It will land as planned." },
  { value: "at_risk", label: "At risk", hint: "It can still land, but something has to change." },
  { value: "off_track", label: "Off track", hint: "It will not land as planned." },
];

/* ---------------------------------------------------------------------------------------------------
 * The two derivations
 * ------------------------------------------------------------------------------------------------- */

/**
 * ⚠️ **Has this key result's post left an outcome nobody knows?** If so it must never be posted again.
 *
 * A goal update is additive and this flow has no delete, so a retry after an indeterminate outcome may
 * leave two contradictory check-ins on one key result, permanently, in a record leadership reads. The
 * server declines to invite a retry (`retryable: false` on `checkin_indeterminate`); this is how the
 * screen still knows on a RELOAD, when the refusal that carried that flag is long gone.
 *
 * ⚠️ **This used to INFER the state from `drafted` plus a non-null `error`, and that was a defect** —
 * a sound reading of the code as it then stood, and wrong. `answerCheckinItem` clears `error` on every
 * re-answer, so a person who re-drafted such an item silently re-armed the post; `posted_update_id`
 * could not stop it either, since that column is null precisely BECAUSE no id could be read back.
 * `indeterminate` is now a state of its own in `server/db/goals.ts`, terminal and enforced in three
 * places that do not depend on a caller remembering. The inference is deleted rather than kept as a
 * fallback: two ways of answering one question is how they come to disagree.
 *
 * The screen holds a session-scoped set alongside this, and still needs to — a post refusal returns an
 * `ApiError` rather than the updated item, so the copy in hand still reads `drafted` until it is
 * re-read. This is the durable half; that is the immediate half.
 */
export function isIndeterminate(item: ItemView): boolean {
  return item.state === "indeterminate";
}

/**
 * Did Atlassian record a different traffic-light from the one intended?
 *
 * The same comparison `server/routes/goals.ts` makes when it sets `mismatch` on a post response, kept
 * here so that a check-in RESUMED in a later session still shows the warning — the response that carried
 * the flag was answered to a page that has since been closed. The screen unions the two; neither can
 * hide a mismatch the other saw.
 */
export function hasMismatch(item: ItemView): boolean {
  if (item.postedState === null || item.newState === null) return false;
  return item.postedState.toLowerCase() !== item.newState;
}

/**
 * Settled: nothing further will happen to this key result in this check-in.
 *
 * `indeterminate` is in here because it is TERMINAL, not because it went well — the server refuses to
 * re-answer or re-post it, so counting it as "still to answer" would put a number on the screen that no
 * amount of answering could ever bring down. `GoalCheckin` counts and names it separately for exactly
 * that reason, and raises a banner for it that no cursor position can hide.
 */
export function isSettled(item: ItemView): boolean {
  return item.state === "posted" || item.state === "skipped" || item.state === "indeterminate";
}

/**
 * Where the walk should open.
 *
 * The first key result still wanting an answer, or — when every one is settled — the last, so that
 * finishing a check-in lands on the end of the list rather than silently back at the top.
 */
export function firstUnsettled(items: readonly ItemView[]): number {
  const found = items.findIndex((item) => !isSettled(item));
  if (found !== -1) return found;
  return items.length === 0 ? 0 : items.length - 1;
}

/** By `position`, which is the order the key results sit under the objective in Atlassian. */
export function inPositionOrder(items: readonly ItemView[]): readonly ItemView[] {
  return [...items].sort((left, right) => left.position - right.position);
}

/* ---------------------------------------------------------------------------------------------------
 * The metric — what kind of number a key result tracks, and how that number is written down
 * ------------------------------------------------------------------------------------------------- */

/**
 * The three ways a figure is written down, transcribed from `server/goals/metrics.ts`'s `METRIC_KINDS`.
 *
 * ⚠️ **The mapping from Atlassian's opaque `metric.type` to one of these belongs to the SERVER and must
 * never be rebuilt on this side of the wire.** The type string is undocumented, its full vocabulary is
 * unknown, and the normalisation is a substring test written once, in one place, and applied where the
 * item is assembled. A second copy here would be a second answer to "is this a percentage", and the two
 * would disagree the first time the gateway sent a shape only one of them had been taught — with the form
 * offering a per-cent sign that the check-in it posts does not use, or the reverse.
 *
 * The item therefore carries `kind` beside `type`: one is the answer, the other is what the gateway
 * actually said, kept so that a reader debugging a figure that formats oddly can see both.
 */
export const METRIC_KINDS = ["percentage", "currency", "number"] as const;
export type MetricKind = (typeof METRIC_KINDS)[number];

/**
 * The kind the server put on a key result's metric — or `number` where none arrived.
 *
 * It takes `unknown` rather than `ItemMetric`, and that is not laziness. `lib/api.ts` is a hand-written
 * transcription of the server's responses, so a field the server has begun sending is present in the JSON
 * and absent from the type until somebody adds it there; reading it through a narrow accessor means this
 * screen renders correctly on the day the server ships it, and goes on compiling on the day `ItemMetric`
 * catches up.
 *
 * ⚠️ The fallback is `number` for the same reason the server's is: an unrecognised — or missing — kind
 * must still produce a usable field, never a crash and never a blank where a figure belongs. It is not
 * the type→kind mapping in disguise: it never looks at `type`, so a metric whose kind failed to arrive
 * reads as a plain number rather than as this file's guess at what Atlassian meant.
 */
export function carriedMetricKind(metric: unknown): MetricKind {
  if (typeof metric !== "object" || metric === null) return "number";
  const carried = (metric as { readonly kind?: unknown }).kind;
  return typeof carried === "string" && (METRIC_KINDS as readonly string[]).includes(carried)
    ? (carried as MetricKind)
    : "number";
}

/**
 * Pinned to a locale rather than left to the browser's, exactly as `server/goals/metrics.ts` pins its own.
 *
 * `Intl.NumberFormat()` with no locale follows the READER's, so the same key result would render "1,200"
 * for one person and "1.200" for the next — and the second is one point two to an English eye. This
 * application's output is English by directive §5 and its figures have to be too. `maximumFractionDigits`
 * is 20 for the server's reason as well: two would round a metric held at 99.999% to 100%, which is a
 * number nobody entered, shown on the screen where they approve what is about to be posted.
 */
const GROUPED = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 20 });

/**
 * The one way a metric figure is written on this screen: "20%", "1,200", or `NOT_READ`.
 *
 * ⚠️ It deliberately says **"Not read"** where its server twin says "not recorded", and the two are not
 * a drift to be reconciled. On the server the phrase describes a figure that is not in the payload the
 * model writes from. Here it describes a synchronised field the sync did not bring back, which
 * `06-modules.md` §5.3 R2 requires be named rather than blanked — and which this screen must keep apart
 * from a key result that tracks no number at all. Same absence, two different facts, two wordings.
 *
 * No forced decimals: 20 is "20%", never "20.0%", because a decimal nobody typed reads as precision
 * nobody has. Currency carries no symbol — the gateway says the metric is money and does not say which.
 */
export function formatMetricValue(value: number | null | undefined, kind: MetricKind): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_READ;
  const text = GROUPED.format(value);
  return kind === "percentage" ? `${text}%` : text;
}

/**
 * "now 20% · started at 0% · target 100%" — a metric as a person reads it, every absent part named.
 *
 * A metric target may carry any subset of current/start/target; a rendering that dropped the missing ones
 * would say "20%" and leave the reader to assume there was nothing else to know. The `kind` is required
 * rather than defaulted: a caller that has an item has its kind too, and a default would quietly render
 * somebody's percentage as a bare number on the one screen where they check it before posting.
 */
export function describeMetric(
  metric: {
    readonly current: number | null;
    readonly start: number | null;
    readonly target: number | null;
  },
  kind: MetricKind,
): string {
  return (
    `now ${formatMetricValue(metric.current, kind)}` +
    ` · started at ${formatMetricValue(metric.start, kind)}` +
    ` · target ${formatMetricValue(metric.target, kind)}`
  );
}

/* ---------------------------------------------------------------------------------------------------
 * The confidence — one number, two notations, and the band it has to sit inside
 * ------------------------------------------------------------------------------------------------- */

/**
 * A state's score band, as `server/atlassian/ids.ts` states it: whole numbers, 1–100.
 *
 * ⚠️ **The bands are the server's and travel with the response.** They are not transcribed here, not
 * defaulted here, and not inferred from the state here. The form's hint, the value it refuses before the
 * round trip and the refusal the server would issue are then three renderings of ONE table; a copy on
 * this side drifts into a form that refuses a value the server would have taken, which reads to the
 * person as the field being broken.
 */
export interface ScoreBand {
  readonly min: number;
  readonly max: number;
  readonly midpoint: number;
}

export type ScoreBands = Readonly<Record<GoalCheckinState, ScoreBand>>;

function integerAt(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function bandAt(source: Record<string, unknown>, state: GoalCheckinState): ScoreBand | null {
  const raw = source[state];
  if (typeof raw !== "object" || raw === null) return null;
  const nested = raw as Record<string, unknown>;
  const min = integerAt(nested, "min");
  const max = integerAt(nested, "max");
  const midpoint = integerAt(nested, "midpoint");
  if (min === null || max === null || midpoint === null) return null;
  // A band that does not contain its own midpoint is not a band, and a form hinting one would send the
  // person hunting for a value the server will refuse. Better to have no field than a field that lies.
  if (min > max || midpoint < min || midpoint > max) return null;
  return { min, max, midpoint };
}

/**
 * The bands the check-in response carried, or `null` if it carried none this screen can trust.
 *
 * `unknown` in, for `carriedMetricKind`'s reason: the field is on the wire before `lib/api.ts` has been
 * taught about it, and this must compile on both sides of that edit. All three states are required —
 * a partial table would leave one traffic-light with a field it could not hint — and every bound must be
 * a whole number, because the wire's contract is integers and a fractional bound would mean the response
 * is not the one we think it is.
 *
 * `null` is a real answer, not a failure: it means this deployment's server does not state the bands, so
 * the screen offers no confidence field at all and the server picks the midpoint, exactly as it did
 * before the field existed. A guessed band would be worse than no field.
 */
export function scoreBandsOf(response: unknown): ScoreBands | null {
  if (typeof response !== "object" || response === null) return null;
  const carried = (response as { readonly scoreBands?: unknown }).scoreBands;
  if (typeof carried !== "object" || carried === null) return null;
  const source = carried as Record<string, unknown>;
  const offTrack = bandAt(source, "off_track");
  const atRisk = bandAt(source, "at_risk");
  const onTrack = bandAt(source, "on_track");
  if (offTrack === null || atRisk === null || onTrack === null) return null;
  return { off_track: offTrack, at_risk: atRisk, on_track: onTrack };
}

/**
 * The confidence as Atlassian shows it: 50 is `0.5`, the figure in the status chip beside a key result.
 *
 * ⚠️ Deliberately the same rendering as `server/goals/refusals.ts`'s `asShownDecimal`, down to 100 being
 * `1` rather than `1.0` — a refusal from the server and the hint under the field are read minutes apart
 * by the same person, and two notations for one number would read as two different fields.
 */
export function scoreAsDecimal(score: number | null | undefined): string {
  if (score === null || score === undefined || !Number.isFinite(score)) return NOT_READ;
  return String(score / 100);
}

/**
 * "At risk spans 0.4-0.6" — the band, in the notation the field takes, and nothing else.
 *
 * ⚠️ THE "Leave blank for 0.5." SENTENCE THAT USED TO FOLLOW IS GONE, on the owner's instruction of
 * 7 August 2026, and dropping it was only safe BECAUSE the field is now seeded with that same midpoint.
 * The sentence existed to tell somebody what an empty box would do; the box is no longer empty, so it
 * told them about a state they would not meet. Blank still means the midpoint — nothing about the
 * behaviour changed, only the paragraph explaining it.
 */
export function scoreBandRange(state: GoalCheckinState, band: ScoreBand): string {
  return `${goalStateLabel(state)} spans ${scoreAsDecimal(band.min)}-${scoreAsDecimal(band.max)}`;
}

/**
 * The same band, said in full, for a REFUSAL — where the reader has just been told no and does need
 * every option spelled out, including the one that costs no decision.
 */
export function scoreBandHint(state: GoalCheckinState, band: ScoreBand): string {
  return `${scoreBandRange(state, band)}. Leave blank for ${scoreAsDecimal(band.midpoint)}.`;
}

/**
 * What the confidence box holds, read as the wire's whole number — or the reason it cannot be.
 *
 * A union rather than `number | null`, because the three ways it can fail want three different sentences
 * and only the screen should be writing sentences. `blank` is a success: it means "the midpoint", which
 * is what the server does with an absent score and what the field's own hint promises.
 */
export type ScoreReading =
  | { readonly outcome: "blank" }
  | { readonly outcome: "score"; readonly score: number }
  | { readonly outcome: "not_a_number" }
  | { readonly outcome: "too_fine" }
  | { readonly outcome: "outside_band" };

/**
 * The decimal a person typed, as the integer the API carries.
 *
 * The rounding is not a nicety: `0.55 * 100` is 55.00000000000001 in IEEE-754, so a bare
 * `Number.isInteger` on the product would refuse a value the field's own hint invites. The product is
 * rounded and the residue compared against a tolerance instead — anything finer than a hundredth (0.555)
 * misses it by half and is refused, which is honest, since the column holds whole numbers and a third
 * decimal place would be silently discarded.
 *
 * The band is checked HERE rather than left to the round trip because the server's refusal costs a
 * request and arrives after the person has stopped looking at the field.
 */
export function readScoreDecimal(text: string, band: ScoreBand): ScoreReading {
  const trimmed = text.trim();
  // Before anything else: `Number("")` is 0, and 0 is a number the band would refuse with a sentence
  // about the band — for a box the person deliberately left empty, meaning "you choose".
  if (trimmed === "") return { outcome: "blank" };

  const decimal = Number(trimmed);
  if (!Number.isFinite(decimal)) return { outcome: "not_a_number" };

  const raw = decimal * 100;
  const score = Math.round(raw);
  if (Math.abs(raw - score) > 1e-6) return { outcome: "too_fine" };
  if (score < band.min || score > band.max) return { outcome: "outside_band" };
  return { outcome: "score", score };
}

/**
 * The draft body as the check-in screen sends it: the answers, plus the confidence the person chose.
 *
 * ⚠️ **`score` now lives in `DraftInput` in `lib/api.ts`, which is where it always belonged**; this
 * intersection was a lean-to against the day it arrived there, and it is kept only so that the check-in
 * screen names the shape it sends rather than inheriting it. That module is the one HTTP seam and
 * `draftCheckinItem` assembles its body field by field — precisely so that `exactOptionalPropertyTypes`
 * cannot smuggle an explicit `undefined` onto the wire — and a field absent from that list is a field the
 * server never sees, however faithfully this screen collects it. The failure is silent: the check-in
 * posts, with the band midpoint, and nobody is told their number was dropped. `score` spent a while in
 * exactly that state. It is asserted at the seam now, not here.
 */
export type CheckinDraftInput = DraftInput & { readonly score?: number };
